/**
 * Migration Page (admin only)
 * Reached at /migration. Lets an admin export the company's current data and
 * import a V9.1 localStorage backup ({ cfg, leads, followups }) — validate first,
 * then commit.
 *
 * Uses the same VITE_API_BASE_URL + Bearer-token convention as the rest of the
 * app. There's no router, so navigation is via window.location.
 */

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

interface MigrationResult {
  branchesCreated: number;
  tariffsCreated: number;
  feesCreated: number;
  leadsCreated: number;
  estimatesCreated: number;
  estimateItemsCreated: number;
  followupsCreated: number;
  activityLogsCreated: number;
  errors: string[];
  warnings: string[];
}

const page: React.CSSProperties = { padding: 20, fontFamily: 'monospace', maxWidth: 720, margin: '0 auto', color: 'var(--crm-text)' };
const card: React.CSSProperties = { background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: 10, padding: 20, marginBottom: 24 };
const btn: React.CSSProperties = { padding: '8px 14px', background: 'var(--crm-accent-fill)', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontFamily: 'monospace' };
const banner = (color: string): React.CSSProperties => ({ padding: 12, border: `1px solid ${color}`, borderRadius: 4, color, margin: '12px 0', fontSize: 13 });

export function MigrationPage() {
  const { user, token } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [report, setReport] = useState<MigrationResult | null>(null);

  if (!user || user.role !== 'admin') {
    return (
      <div style={page}>
        <h2>Access denied</h2>
        <p style={{ color: 'var(--crm-muted)' }}>Only admins can access the migration tools.</p>
        <button style={btn} onClick={() => window.location.assign('/')}>← Back</button>
      </div>
    );
  }

  const authHeaders = { Authorization: `Bearer ${token}` };

  const readBackup = async (): Promise<any> => {
    if (!file) throw new Error('Select a backup file first');
    const text = await file.text();
    try {
      return JSON.parse(text);
    } catch {
      throw new Error('Selected file is not valid JSON');
    }
  };

  const post = async (path: string, backup: any, dryRun: boolean): Promise<MigrationResult> => {
    const res = await fetch(`${API_BASE_URL}/api/migration/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ backup, dryRun }),
    });
    const json = await res.json();
    if (!res.ok || !json.success) throw new Error(json?.error?.message || `Request failed (${res.status})`);
    return json.data as MigrationResult;
  };

  const handleValidate = async () => {
    setLoading(true); setError(null); setSuccess(null); setReport(null);
    try {
      const data = await post('validate', await readBackup(), true);
      setReport(data);
      setSuccess(
        data.errors.length
          ? 'Validated with errors — resolve them before importing.'
          : 'Validation passed. Review the report, then Import.'
      );
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!report) { setError('Validate first'); return; }
    if (report.errors.length && !window.confirm('Validation reported errors; those records will be skipped. Import the rest anyway?')) return;
    if (!window.confirm('This writes data into the live database and cannot be automatically undone. Continue?')) return;

    setLoading(true); setError(null); setSuccess(null);
    try {
      const data = await post('import', await readBackup(), false);
      setReport(data);
      setSuccess(
        `Import complete — ${data.leadsCreated} leads, ${data.branchesCreated} branches, ` +
        `${data.tariffsCreated} tariffs, ${data.feesCreated} fees, ${data.followupsCreated} follow-ups.`
      );
      setFile(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setLoading(true); setError(null); setSuccess(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/migration/export`, { headers: authHeaders });
      if (!res.ok) {
        let msg = `Export failed (${res.status})`;
        try { msg = (await res.json())?.error?.message || msg; } catch { /* non-JSON */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `moveflow-backup-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setSuccess('Backup downloaded.');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1>Data Migration</h1>
        <button style={{ ...btn, background: 'var(--crm-line)', padding: '4px 8px' }} onClick={() => window.location.assign('/')}>← Back</button>
      </div>
      <p style={{ color: 'var(--crm-muted)', fontSize: 12 }}>Export current data, or import a V9.1 backup.</p>

      <div style={card}>
        <h3 style={{ marginTop: 0 }}>Export current data</h3>
        <p style={{ color: 'var(--crm-muted)', fontSize: 13 }}>Downloads a JSON snapshot of this company's data.</p>
        <button style={btn} onClick={handleExport} disabled={loading}>{loading ? 'Working…' : 'Export backup'}</button>
      </div>

      <div style={card}>
        <h3 style={{ marginTop: 0 }}>Import V9.1 backup</h3>
        <input
          type="file"
          accept=".json,application/json"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setReport(null); setSuccess(null); setError(null); }}
          style={{ display: 'block', marginBottom: 12, color: 'var(--crm-text)' }}
        />
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={btn} onClick={handleValidate} disabled={!file || loading}>{loading ? 'Working…' : 'Validate'}</button>
          <button
            style={{ ...btn, background: report ? 'var(--crm-green-fill)' : 'var(--crm-line)' }}
            onClick={handleImport}
            disabled={!report || loading}
          >
            Import
          </button>
        </div>

        {error && <div style={banner('var(--crm-red)')}>{error}</div>}
        {success && <div style={banner('var(--crm-green)')}>{success}</div>}

        {report && (
          <div style={{ ...card, marginTop: 16, marginBottom: 0, background: 'var(--crm-panel-2)' }}>
            <h4 style={{ marginTop: 0 }}>Report</h4>
            <div style={{ fontSize: 12, color: 'var(--crm-muted)', lineHeight: 1.9 }}>
              <div>Branches: {report.branchesCreated}</div>
              <div>Tariffs: {report.tariffsCreated}</div>
              <div>Fees: {report.feesCreated}</div>
              <div>Leads: {report.leadsCreated}</div>
              <div>Estimates: {report.estimatesCreated} ({report.estimateItemsCreated} items)</div>
              <div>Activity logs: {report.activityLogsCreated}</div>
              <div>Follow-ups: {report.followupsCreated}</div>
            </div>
            {report.warnings.length > 0 && (
              <div style={{ marginTop: 10, color: 'var(--crm-amber)', fontSize: 12 }}>
                <strong>Warnings ({report.warnings.length}):</strong>
                {report.warnings.map((w, i) => <div key={i}>• {w}</div>)}
              </div>
            )}
            {report.errors.length > 0 && (
              <div style={{ marginTop: 10, color: 'var(--crm-red)', fontSize: 12 }}>
                <strong>Errors ({report.errors.length}):</strong>
                {report.errors.map((e, i) => <div key={i}>• {e}</div>)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
